// =============================================================
// VARIANCE — The Executive Command Center
// File      : Settings.tsx
// Location  : /home/Applications/variance/web/src/pages/Settings.tsx
// Purpose   : Premium dark-glass Settings page with RBAC management.
// =============================================================

import { useState, useEffect } from 'react';
import { LayoutDashboard, Monitor, AlignLeft, Check, Palette, HelpCircle, Shield, Plus, X } from 'lucide-react';
import { applyTheme, getSavedTheme, getSavedShell, saveShell, Theme, ShellType } from '../lib/theme';
import { useHelp } from '../components/HelpSystem';

const MODULES = ['Cockpit', 'Companies', 'Sales Hub', 'Projects', 'Assets', 'Vendors', 'Finance', 'Compliance', 'HR', 'Reports', 'Settings'];

export function Settings() {
  // --- TABS STATE ---
  const [activeTab, setActiveTab] = useState<'appearance' | 'guidance' | 'access' | 'users'>('appearance');

  // --- EXISTING UI STATE ---
  const [shell, setShell] = useState<ShellType>('shell-classic');
  const [theme, setTheme] = useState<Theme>('theme-dark');

  // --- HELP STATE ---
  const { isHelpEnabled, toggleHelp } = useHelp();

  // --- AUTHORISATION STATE ---
  const [accessLevels, setAccessLevels] = useState<any[]>([]);
  const [staffList, setStaffList] = useState<any[]>([]);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleModules, setNewRoleModules] = useState<string[]>([]);

  useEffect(() => {
    setShell(getSavedShell());
    setTheme(getSavedTheme());
  }, []);

  const fetchAuthData = () => {
    fetch('/api/hr/access-levels').then(res => res.json()).then(data => {
      if (Array.isArray(data)) setAccessLevels(data);
    });
    fetch('/api/hr/permissions').then(res => res.json()).then(data => {
      if (Array.isArray(data)) setStaffList(data);
    });
  };

  useEffect(() => {
    if (activeTab === 'access' || activeTab === 'users') fetchAuthData();
  }, [activeTab]);

  const handleShellChange = (newShell: ShellType) => {
    setShell(newShell);
    saveShell(newShell);
    window.dispatchEvent(new Event('shellChanged'));
  };

  const handleThemeChange = (newTheme: Theme) => {
    setTheme(newTheme);
    applyTheme(newTheme);
  };

  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch('/api/hr/access-levels', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ level_name: newRoleName, allowed_modules: newRoleModules })
    });
    setIsRoleModalOpen(false);
    setNewRoleName('');
    setNewRoleModules([]);
    fetchAuthData();
  };

  const toggleModule = (mod: string) => {
    setNewRoleModules(prev => 
      prev.includes(mod) ? prev.filter(m => m !== mod) : [...prev, mod]
    );
  };

  const handleAssignRole = async (staffId: string, accessLevelId: string) => {
    await fetch('/api/hr/permissions', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ staff_id: staffId, access_level_id: accessLevelId })
    });
    fetchAuthData();
  };

  return (
    <div className="space-y-8 max-w-6xl">
      
      {/* HEADER */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>Settings</h1>
        <p className="text-sm mt-2 tracking-wide" style={{ color: 'var(--text-secondary)' }}>Manage your preferences, system access, and user authorisations.</p>
      </div>

      {/* TABS NAVIGATION */}
      <div className="flex border-b space-x-8 overflow-x-auto" style={{ borderColor: 'var(--border-subtle)' }}>
        <button onClick={() => setActiveTab('appearance')} className={`flex items-center gap-2 pb-3 px-1 font-semibold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'appearance' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-400 hover:text-gray-200'}`}>
          <Palette size={18} /> Appearance
        </button>
        <button onClick={() => setActiveTab('guidance')} className={`flex items-center gap-2 pb-3 px-1 font-semibold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'guidance' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-400 hover:text-gray-200'}`}>
          <HelpCircle size={18} /> Guidance
        </button>
        <button onClick={() => setActiveTab('access')} className={`flex items-center gap-2 pb-3 px-1 font-semibold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'access' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-400 hover:text-gray-200'}`}>
          <Shield size={18} /> System Access Levels
        </button>
        <button onClick={() => setActiveTab('users')} className={`flex items-center gap-2 pb-3 px-1 font-semibold border-b-2 transition-colors whitespace-nowrap ${activeTab === 'users' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-400 hover:text-gray-200'}`}>
          <LayoutDashboard size={18} /> User Authorisations
        </button>
      </div>

      {/* TAB 1: APPEARANCE (YOUR EXISTING UI) */}
      {activeTab === 'appearance' && (
        <div className="space-y-10 animate-in fade-in duration-300">
          {/* Layout Selection */}
          <div className="p-8 rounded-2xl" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
            <h2 className="text-lg font-semibold mb-6" style={{ color: 'var(--text-primary)' }}>Canvas Layout</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <button onClick={() => handleShellChange('shell-classic')} className={`p-6 rounded-xl flex flex-col items-center gap-4 border-2 ${shell === 'shell-classic' ? 'border-[var(--brand-accent)]' : 'border-[var(--border-subtle)]'}`} style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <LayoutDashboard size={40} />
                <span className="font-bold">Classic Command</span>
              </button>
              <button onClick={() => handleShellChange('shell-immersive')} className={`p-6 rounded-xl flex flex-col items-center gap-4 border-2 ${shell === 'shell-immersive' ? 'border-[var(--brand-accent)]' : 'border-[var(--border-subtle)]'}`} style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <AlignLeft size={40} />
                <span className="font-bold">Immersive Canvas</span>
              </button>
              <button onClick={() => handleShellChange('shell-topnav')} className={`p-6 rounded-xl flex flex-col items-center gap-4 border-2 ${shell === 'shell-topnav' ? 'border-[var(--brand-accent)]' : 'border-[var(--border-subtle)]'}`} style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <Monitor size={40} />
                <span className="font-bold">Top-Nav Executive</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: GUIDANCE */}
      {activeTab === 'guidance' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="p-8 rounded-2xl flex items-center justify-between" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
            <div>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Contextual Help System</h2>
              <p className="text-sm max-w-xl" style={{ color: 'var(--text-secondary)' }}>Enable or disable help banners and tooltips.</p>
            </div>
            <button onClick={toggleHelp} className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${isHelpEnabled ? 'bg-[var(--brand-accent)]' : 'bg-gray-600'}`}>
              <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${isHelpEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>
      )}

      {/* TAB 3: SYSTEM ACCESS LEVELS */}
      {activeTab === 'access' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="p-8 rounded-2xl" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>System Access Levels</h2>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Define what modules each access level can view.</p>
              </div>
              <button onClick={() => setIsRoleModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded text-white font-bold" style={{ backgroundColor: 'var(--brand-accent)' }}>
                <Plus size={18} /> Create Access Level
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {accessLevels.map(level => (
                <div key={level.id} className="p-4 rounded-xl border border-[var(--border-subtle)]" style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                  <h3 className="text-white font-bold mb-2">{level.level_name}</h3>
                  <div className="flex flex-wrap gap-1">
                    {level.allowed_modules.map((m: string) => (
                      <span key={m} className="text-[10px] px-2 py-0.5 rounded bg-[var(--bg-canvas)] text-gray-300 border border-[var(--border-subtle)]">{m}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: USER AUTHORISATIONS */}
      {activeTab === 'users' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="p-8 rounded-2xl" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
            <h2 className="text-lg font-semibold mb-6" style={{ color: 'var(--text-primary)' }}>User Authorisations</h2>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                  <th className="p-4 text-xs font-semibold uppercase text-gray-400">Employee</th>
                  <th className="p-4 text-xs font-semibold uppercase text-gray-400">Email</th>
                  <th className="p-4 text-xs font-semibold uppercase text-gray-400 w-72">System Access Level</th>
                </tr>
              </thead>
              <tbody>
                {staffList.map(s => (
                  <tr key={s.id} className="border-t border-[var(--border-subtle)]">
                    <td className="p-4 text-white font-medium">{s.full_name}</td>
                    <td className="p-4 text-gray-400 text-sm">{s.email}</td>
                    <td className="p-4">
                      <select 
                        value={s.access_level_id || ''} 
                        onChange={(e) => handleAssignRole(s.id, e.target.value)}
                        className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white"
                      >
                        <option value="">No Access Assigned</option>
                        {accessLevels.map(level => (
                          <option key={level.id} value={level.id}>{level.level_name}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CREATE ROLE MODAL */}
      {isRoleModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1 absolute inset-0" onClick={() => setIsRoleModalOpen(false)}></div>
          <div className="w-full max-w-lg bg-[var(--bg-panel)] rounded-xl border border-[var(--border-subtle)] relative z-10 p-6">
            <h2 className="text-xl font-bold text-white mb-4">Create System Access Level</h2>
            <form onSubmit={handleCreateRole} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Level Name (e.g. VP Sales, HOD Finance)</label>
                <input required type="text" value={newRoleName} onChange={e => setNewRoleName(e.target.value)} className="w-full bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-2">Allowed Modules</label>
                <div className="grid grid-cols-2 gap-2">
                  {MODULES.map(m => (
                    <label key={m} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={newRoleModules.includes(m)} 
                        onChange={() => toggleModule(m)} 
                        className="rounded bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-[var(--brand-accent)] focus:ring-0"
                      />
                      {m}
                    </label>
                  ))}
                </div>
              </div>
              <button type="submit" className="w-full py-2 rounded text-white font-bold" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Access Level</button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
